import { useEffect, useRef, useState } from "react";
import { Globe, Loader2, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

/** Radix Select can't use "" as a value, so no-category gets a sentinel. */
const NO_CATEGORY = "__none__";

const EMPTY = {
  name: "",
  url: "",
  icon_url: "",
  category_id: "",
  description: "",
  admin_only: false,
  allowed_emails: [],
};

export default function AppFormModal({ open, onClose, onSave, onDelete, app, categories = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // The API returns null for unset columns; the form wants empty strings.
    setForm(
      app ? Object.fromEntries(Object.keys(EMPTY).map((key) => [key, app[key] ?? EMPTY[key]])) : EMPTY,
    );
    setEmailInput("");
  }, [app, open]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const withBusy = async (label, fn) => {
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(null);
    }
  };

  const uploadIcon = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    withBusy("upload", async () => set({ icon_url: await api.apps.uploadIcon(file) }));
  };

  const iconFromWebsite = () =>
    withBusy("favicon", async () => {
      set({ icon_url: await api.apps.iconFromUrl(form.url) });
      toast.success("Icon saved from the website");
    });

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (email && !form.allowed_emails.includes(email)) {
      set({ allowed_emails: [...form.allowed_emails, email] });
    }
    setEmailInput("");
  };

  const removeEmail = (email) =>
    set({ allowed_emails: form.allowed_emails.filter((e) => e !== email) });

  const canSave = form.name.trim() && form.url.trim();

  const save = async () => {
    if (!canSave) return;
    setBusy("save");
    try {
      await onSave({
        name: form.name,
        url: form.url,
        category_id: form.category_id || null,
        icon_url: form.icon_url,
        description: form.description,
        admin_only: form.admin_only,
        allowed_emails: form.allowed_emails,
      });
      onClose();
    } catch {
      /* onSave already surfaced the error */
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{app ? "Edit app" : "Add app"}</DialogTitle>
          <DialogDescription>Apps open in a new browser tab.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="app-name">Name *</Label>
            <Input
              id="app-name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. DocketManager"
            />
          </div>

          <div>
            <Label htmlFor="app-url">Address *</Label>
            <Input
              id="app-url"
              value={form.url}
              onChange={(e) => set({ url: e.target.value })}
              placeholder="docketmanager.com"
              spellCheck={false}
            />
            <p className="text-xs text-gray-500 mt-1">
              The site's icon is fetched automatically when you save.
            </p>
          </div>

          <div>
            <Label>Icon</Label>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {/* Chequerboard so a transparent icon reads as transparent here,
                  matching how it will sit on the grid. */}
              <div
                className="w-12 h-12 rounded-xl border flex items-center justify-center overflow-hidden shrink-0 p-1"
                style={{
                  backgroundImage:
                    "linear-gradient(45deg,#e5e7eb 25%,transparent 25%,transparent 75%,#e5e7eb 75%)," +
                    "linear-gradient(45deg,#e5e7eb 25%,transparent 25%,transparent 75%,#e5e7eb 75%)",
                  backgroundSize: "12px 12px",
                  backgroundPosition: "0 0, 6px 6px",
                }}
              >
                {form.icon_url ? (
                  <img src={form.icon_url} alt="" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-xl text-gray-500">{form.name.charAt(0).toUpperCase() || "?"}</span>
                )}
              </div>

              <input
                ref={fileRef}
                type="file"
                // image/* keeps the picker from greying out formats the server
                // accepts, SVG in particular.
                accept="image/*,.svg,.ico"
                className="hidden"
                onChange={uploadIcon}
              />
              {/* Each button waits on its own action only. Disabling on any busy
                  state meant a fetch running in the background blocked uploads. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={busy === "upload"}
              >
                {busy === "upload" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                Upload
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={iconFromWebsite}
                disabled={busy === "favicon" || !form.url}
              >
                {busy === "favicon" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Globe className="w-3.5 h-3.5 mr-1" />}
                From website
              </Button>

              {form.icon_url && (
                <button type="button" onClick={() => set({ icon_url: "" })} className="text-gray-400 hover:text-red-500" title="Remove icon">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div>
            <Label>Category</Label>
            <Select
              value={form.category_id || NO_CATEGORY}
              onValueChange={(v) => set({ category_id: v === NO_CATEGORY ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a category…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="app-desc">Description</Label>
            <Textarea
              id="app-desc"
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Short description…"
              rows={2}
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch id="app-admin" checked={form.admin_only} onCheckedChange={(v) => set({ admin_only: v })} />
            <Label htmlFor="app-admin" className="mb-0">Admins only</Label>
          </div>

          <div>
            <Label>Restrict to specific people</Label>
            <p className="text-xs text-gray-500 mb-2">Leave empty so everyone signed in can see it.</p>
            <div className="flex gap-2">
              <Input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail();
                  }
                }}
                placeholder="person@goldstarprinting.us"
                className="flex-1"
              />
              <Button type="button" variant="outline" size="icon" onClick={addEmail}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.allowed_emails.map((email) => (
                <span key={email} className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs">
                  {email}
                  <button type="button" onClick={() => removeEmail(email)} aria-label={`Remove ${email}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          {app && (
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(app.id);
                onClose();
              }}
              className="flex-1"
            >
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave || busy === "save"} className="flex-1">
            {busy === "save" ? "Saving…" : app ? "Update" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
